import React, { useEffect, useState, useRef, useCallback } from "react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading.js";
import { secretKey } from "./data";
import { downloadExcel } from "react-export-table-to-excel";
import MultiSelect from "./multiSelect.js";

const MySwal = withReactContent(Swal2);

const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const PAGE_SIZES = [25, 50, 100, 200];

/* ─── debounce hook ─── */
function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback(
    (...args) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
}

/* ─── note cell ─── */
const NoteCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span className="lq-empty">—</span>;
  const isLong = text.length > 80;
  return (
    <div
      className={`lq-note ${expanded ? "expanded" : ""}`}
      onClick={() => isLong && setExpanded(!expanded)}
      title={isLong && !expanded ? text : undefined}
    >
      {expanded || !isLong ? text : `${text.slice(0, 80)}…`}
      {isLong && (
        <button className="lq-note-toggle" type="button">
          {expanded ? "zwiń" : "więcej"}
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */

const Doubles = (props) => {
  const isAdmin = props.user.role === "admin";

  /* ── data ── */
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /* ── pagination ── */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  /* ── sorting ── */
  const [sort, setSort] = useState("date");
  const [sortDir, setSortDir] = useState("DESC");

  /* ── filters ── */
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [filterSource, setFilterSource] = useState([]);
  const [selectedOptionsSource, setSelectedOptionsSource] = useState([]);
  const [isHoveringSource, setIsHoveringSource] = useState(false);

  const [filterCompany, setFilterCompany] = useState([]);
  const [selectedOptionsCompany, setSelectedOptionsCompany] = useState([]);
  const [isHoveringCompany, setIsHoveringCompany] = useState(false);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  /* ── filter options ── */
  const [sources, setSources] = useState([]);
  const [companies, setCompanies] = useState([]);

  /* ═══════════════════════════════════════════════════════ */
  /*  FETCH
  /* ═══════════════════════════════════════════════════════ */

  /* ── init: filter options (once) ── */
  const fetchFilterData = async () => {
    try {
      const res = await fetch(`${API}/getDubFilterData`, {
        method: "POST",
        headers: hdrs,
        body: "{}",
      });
      const data = await res.json();
      setSources(data.sources || []);
      setCompanies(data.companies || []);
    } catch (err) {
      console.error("fetchFilterData:", err);
    }
  };

  /* ── paginated data ── */
  const fetchLeads = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const body = {
          page: overrides.page ?? page,
          limit: overrides.limit ?? limit,
          search: overrides.search ?? search,
          sources: overrides.sources ?? filterSource,
          companies: overrides.companies ?? filterCompany,
          dateFrom: overrides.dateFrom ?? dateFrom,
          dateTo: overrides.dateTo ?? dateTo,
          sort: overrides.sort ?? sort,
          sortDir: overrides.sortDir ?? sortDir,
        };
        const res = await fetch(`${API}/getLeadsDubPaginated`, {
          method: "POST",
          headers: hdrs,
          body: JSON.stringify(body),
        });
        const data = await res.json();
        setLeads(data.data || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 0);
      } catch (err) {
        console.error("fetchLeads:", err);
      }
      setLoading(false);
    },
    [
      page,
      limit,
      search,
      filterSource,
      filterCompany,
      dateFrom,
      dateTo,
      sort,
      sortDir,
    ]
  );

  /* ── init ── */
  useEffect(() => {
    (async () => {
      await Promise.all([fetchFilterData(), fetchLeads()]);
      setInitLoading(false);
    })();
  }, []);

  /* ── refetch on page / limit / sort change ── */
  useEffect(() => {
    if (initLoading) return;
    fetchLeads();
  }, [page, limit, sort, sortDir]);

  /* ═══════════════════════════════════════════════════════ */
  /*  HANDLERS
  /* ═══════════════════════════════════════════════════════ */

  /* ── debounced search ── */
  const debouncedSearch = useDebounce((val) => {
    setSearch(val);
    setPage(1);
    fetchLeads({ search: val, page: 1 });
  }, 400);

  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  /* ── filter change → reset page 1 → fetch ── */
  const applyFilter = (overrides) => {
    setPage(1);
    fetchLeads({ ...overrides, page: 1 });
  };

  const handleSourceChange = (val) => {
    setFilterSource(val);
    applyFilter({ sources: val });
  };

  const handleCompanyChange = (val) => {
    setFilterCompany(val);
    applyFilter({ companies: val });
  };

  const handleDateApply = () => {
    if (!dateFrom || !dateTo) return;
    if (new Date(dateFrom) > new Date(dateTo)) {
      MySwal.fire({
        icon: "error",
        title: "Błąd dat",
        text: '"Od" nie może być większe niż "Do"',
      });
      return;
    }
    applyFilter({ dateFrom, dateTo });
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setFilterSource([]);
    setSelectedOptionsSource([]);
    setFilterCompany([]);
    setSelectedOptionsCompany([]);
    setDateFrom("");
    setDateTo("");
    setSort("date");
    setSortDir("DESC");
    setPage(1);
    fetchLeads({
      search: "",
      sources: [],
      companies: [],
      dateFrom: "",
      dateTo: "",
      sort: "date",
      sortDir: "DESC",
      page: 1,
    });
  };

  /* ── sorting ── */
  const handleColumnSort = (col) => {
    if (sort === col) {
      setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    } else {
      setSort(col);
      setSortDir("DESC");
    }
  };

  const SortIcon = ({ col }) => {
    if (sort !== col) return <span className="pg-sort-icon">↕</span>;
    return (
      <span className="pg-sort-icon pg-sort-active">
        {sortDir === "ASC" ? "↑" : "↓"}
      </span>
    );
  };

  /* ── hovering ── */
  const closeAllHovering = (e) => {
    const id = e.target?.id;
    if (id !== "source") setIsHoveringSource(false);
    if (id !== "company") setIsHoveringCompany(false);
  };

  /* ── excel export (current page) ── */
  const exportToExcel = () => {
    const header = [
      "ID",
      "Nazwa",
      "Telefon",
      "E-mail",
      "Źródło",
      "Data",
      "Uwagi",
      "Dział",
      "Przyczyna",
    ];
    const body = leads.map((el) => [
      el.id,
      el.name,
      el.phone,
      el.mail,
      el.source,
      el.date,
      el.sourceNote,
      el.company,
      el.doubleReason,
    ]);
    downloadExcel({
      fileName: "duplikaty_" + new Date().toISOString().slice(0, 10),
      sheet: "data",
      tablePayload: { header, body },
    });
  };

  /* ── option builders ── */
  const toOptions = (arr) => [
    { label: "Wszystkie", value: "all" },
    ...arr.map((el) => ({ label: el.name, value: el.name })),
  ];

  /* ── page numbers ── */
  const pageNumbers = () => {
    const pages = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    if (start > 1) pages.push(1);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("...");
    if (end < totalPages) pages.push(totalPages);
    return pages;
  };

  /* ═══════════════════════════════════════════════════════ */
  /*  RENDER
  /* ═══════════════════════════════════════════════════════ */

  if (initLoading) return <Loading />;

  return (
    <div className="db-wrapper" onClick={closeAllHovering}>
      {/* ── Toolbar ── */}
      <div className="lq-toolbar">
        <div className="lq-toolbar-left">
          <button
            className="lq-btn lq-btn--success"
            onClick={() => fetchLeads()}
          >
            Odśwież
          </button>
          {isAdmin && (
            <button className="lq-btn lq-btn--dark" onClick={exportToExcel}>
              Eksport
            </button>
          )}
          <div className="db-search-field">
            <label className="db-field-label">Szukaj</label>
            <input
              type="text"
              className="db-input"
              value={searchInput}
              onChange={handleSearchInput}
              placeholder="nazwa, tel., mail..."
            />
          </div>
        </div>
        <div className="lq-toolbar-left">
          <button
            className={`lq-btn ${
              showFilters ? "lq-btn--primary" : "lq-btn--muted"
            }`}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Zwiń filtry" : "Filtry"}
          </button>
          <span className="lq-count">
            {total.toLocaleString("pl-PL")} duplikatów
          </span>
        </div>
      </div>

      {/* ── Filters ── */}
      {showFilters && (
        <div className="db-filters">
          <div className="db-filters-selects">
            <div className="db-filter-group">
              <label className="db-field-label">Źródło</label>
              <MultiSelect
                options={toOptions(sources)}
                value={filterSource}
                onChange={handleSourceChange}
                selectedOptions={selectedOptionsSource}
                setSelectedOptions={setSelectedOptionsSource}
                isHovering={isHoveringSource}
                setIsHovering={setIsHoveringSource}
                id="source"
                closeAllHovering={closeAllHovering}
              />
            </div>
            {isAdmin && (
              <div className="db-filter-group">
                <label className="db-field-label">Dział</label>
                <MultiSelect
                  options={toOptions(companies)}
                  value={filterCompany}
                  onChange={handleCompanyChange}
                  selectedOptions={selectedOptionsCompany}
                  setSelectedOptions={setSelectedOptionsCompany}
                  isHovering={isHoveringCompany}
                  setIsHovering={setIsHoveringCompany}
                  id="company"
                  closeAllHovering={closeAllHovering}
                />
              </div>
            )}
          </div>
          <div className="db-filters-dates">
            <div className="db-filter-date-group">
              <label className="db-field-label">Data pozyskania</label>
              <div className="db-date-range">
                <div className="db-date-field">
                  <span className="db-date-label">od</span>
                  <input
                    type="date"
                    className="db-input"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="db-date-field">
                  <span className="db-date-label">do</span>
                  <input
                    type="date"
                    className="db-input"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <button
                  className="lq-btn lq-btn--dark db-date-apply"
                  onClick={handleDateApply}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
          <div className="db-filters-footer">
            <button className="lq-btn lq-btn--muted" onClick={clearFilters}>
              Wyczyść filtry
            </button>
          </div>
        </div>
      )}

      {/* ── Pagination top ── */}
      <div className="pg-bar">
        <div className="pg-bar-left">
          <label className="db-field-label">Na stronę</label>
          <select
            className="db-input db-select pg-limit-select"
            value={limit}
            onChange={(e) => {
              setLimit(+e.target.value);
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span className="pg-info">
            {((page - 1) * limit + 1).toLocaleString("pl-PL")}–
            {Math.min(page * limit, total).toLocaleString("pl-PL")} z{" "}
            {total.toLocaleString("pl-PL")}
          </span>
        </div>
        <div className="pg-pages">
          <button
            className="pg-btn"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            ‹
          </button>
          {pageNumbers().map((p, i) =>
            p === "..." ? (
              <span key={`e${i}`} className="pg-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`pg-btn ${p === page ? "pg-btn--active" : ""}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            className="pg-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            ›
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="lq-table-wrap db-table-scroll">
        {loading ? (
          <div className="pg-loading">Ładowanie...</div>
        ) : (
          <table className="lq-table">
            <thead>
              <tr>
                <th
                  className="pg-th-sort"
                  onClick={() => handleColumnSort("id")}
                >
                  ID <SortIcon col="id" />
                </th>
                <th
                  className="pg-th-sort"
                  onClick={() => handleColumnSort("name")}
                >
                  Nazwa <SortIcon col="name" />
                </th>
                <th>Telefon</th>
                <th>E-mail</th>
                <th
                  className="pg-th-sort"
                  onClick={() => handleColumnSort("source")}
                >
                  Źródło <SortIcon col="source" />
                </th>
                <th
                  className="pg-th-sort"
                  onClick={() => handleColumnSort("date")}
                >
                  Data <SortIcon col="date" />
                </th>
                <th>Uwagi</th>
                <th
                  className="pg-th-sort"
                  onClick={() => handleColumnSort("company")}
                >
                  Dział <SortIcon col="company" />
                </th>
                <th
                  className="pg-th-sort"
                  onClick={() => handleColumnSort("doubleReason")}
                >
                  Przyczyna <SortIcon col="doubleReason" />
                </th>
              </tr>
            </thead>
            <tbody>
              {leads.map((el) => (
                <tr key={el.id}>
                  <td className="lq-td-id">{el.id}</td>
                  <td>{el.name}</td>
                  <td className="lq-td-mono">{el.phone}</td>
                  <td className="lq-td-mono">{el.mail}</td>
                  <td>{el.source}</td>
                  <td className="lq-td-mono">{el.date}</td>
                  <td className="lq-td-note">
                    <NoteCell text={el.sourceNote} />
                  </td>
                  <td>{el.company}</td>
                  <td>
                    <span className="db-reason-badge">{el.doubleReason}</span>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr>
                  <td colSpan={9} className="lq-empty-row">
                    Brak duplikatów
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination bottom ── */}
      {totalPages > 1 && (
        <div className="pg-bar pg-bar--bottom">
          <span className="pg-info">
            Strona {page} z {totalPages}
          </span>
          <div className="pg-pages">
            <button
              className="pg-btn"
              disabled={page <= 1}
              onClick={() => setPage(1)}
            >
              «
            </button>
            <button
              className="pg-btn"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              ‹
            </button>
            <button
              className="pg-btn"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              ›
            </button>
            <button
              className="pg-btn"
              disabled={page >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Doubles;
